import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Settings, APP_CONFIG } from '../config/settings';
import { combine, arrayToQuery, appendQuery, fetchArgsToQueryString } from '../shared/utilities/route';
import { PagingToken, FetchArgs } from '../shared/models/core';
import { Observable } from 'rxjs';
import { ClinicalReview } from './review-queue/models/clinical-review.model';
import { ClinicalReviewLinkDetails } from './review-queue/models/clinical-review-link-details.model';
import { ClinicalReviewNoteDetails } from './review-queue/models/clinical-review-note-details.model';
import { AssignClinicalReviews } from './review-queue/models/assign-clinical-reviews.model';
import { InProgressReviewQueueDto } from './review-queue/models/InProgressReviewQueueDto';
import { ClinicalReviewHistory } from './review-queue/models/clinical-review-history.model';
import { UdConfiguration } from './unit-description-configuration/models/ud-configuration.model';
import { ProfessionSpecialtyAssessment } from './unit-description-configuration/models/profession-specialty-assessment.model';
import {
    LookupDto,
    UdConfigurationDetails
} from './unit-description-configuration/models/ud-configuration-details.model';
import { UpsertUnitDescriptionConfigurationDto } from './unit-description-configuration/models/upsert-ud-configuration.model';
import { ClinicalReviewHistoryByFacility } from './review-queue/models/clinical-review-history-by-facility.model';
import { ClinicalTrackAddDto, ClinicalTrackConfigurations } from './clinical-track-configurations/models';
import {
    ClinicalTrackAssessmentCategorySkills,
    ClinicalTrackConfigurationDetails
} from './clinical-track-configurations/models/clinical-track-configuration-details.model';
import { ClinicalTrackConfigurationUpdateDto } from './clinical-track-configurations/models/clinical-track-update.model';
import { UdAssessmentCertsAndSkills } from './unit-description-configuration/models/ud-configuration-assessment-certs-and-skills.model';
import { ClinicalReviewSubmittalStatus } from './review-queue/models/clinical-review-submittal-status.model';
import { ClinicalReviewReassignReasonLookup } from './review-queue/models/clinical-review-reassign-reason-lookup.model';
import { ReassignClinicalReviews } from './review-queue/models/reassign-clinical-reviews.model';
import { ClinicalReviewMatchHistory } from 'src/app/recruiting/candidates/submittal/models/clinical-review-match-history.model';
import { SubmittalJobQualifications } from '../recruiting/candidates/submittal/models/submittal-job-qualifications.model';
import { LineOfBusinessDto } from './review-queue/models/LineOfBusinessDto';
import { AvailableSkillsDto } from 'src/app/shared/components/job-qualifications/models/available-skills.model';
import { AvailableCertsDto } from 'src/app/shared/components/job-qualifications/models/available-certs.model';

export interface ValidationResult {
    name: string;
    isValid?: boolean;
    message?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ClinicalService {
    private coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    getClinicalTrackConfigurations(args: FetchArgs): Observable<PagingToken<ClinicalTrackConfigurations[]>> {
        const url = `${this.coreApiPath}/clinical/clinical-track-configurations`;
        const query = fetchArgsToQueryString(args);
        return this.http.get<PagingToken<ClinicalTrackConfigurations[]>>(`${url}${query}`);
    }

    getClinicalTrackConfigurationById(id: number): Observable<ClinicalTrackConfigurationDetails> {
        const url = `${this.coreApiPath}/clinical/clinical-track-configurations/${id}`;
        return this.http.get<ClinicalTrackConfigurationDetails>(url);
    }

    getSkillsByAssessmentId(
        clinicalTrackId: number,
        assessmentId: number
    ): Observable<ClinicalTrackAssessmentCategorySkills[]> {
        const url = `${this.coreApiPath}/clinical/clinical-track-configurations/${clinicalTrackId}/assessment-categories-skills/${assessmentId}`;
        return this.http.get<ClinicalTrackAssessmentCategorySkills[]>(url);
    }

    getSkillsByClinicalTrackId(clinicalTrackId: number): Observable<AvailableSkillsDto[]> {
        const url = `${this.coreApiPath}/clinical/clinical-track-configurations/${clinicalTrackId}/skills`;
        return this.http.get<AvailableSkillsDto[]>(url);
    }

    getCertificationsByClinicalTrackId(clinicalTrackId: number): Observable<AvailableCertsDto[]> {
        const url = `${this.coreApiPath}/clinical/clinical-track-configurations/${clinicalTrackId}/certifications`;
        return this.http.get<AvailableCertsDto[]>(url);
    }

    updateClinicalTrackConfigurationById(dto: ClinicalTrackConfigurationUpdateDto): Observable<void> {
        const id = dto.clinicalTrackConfigurationId;
        const url = `${this.coreApiPath}/clinical/clinical-track-configurations/${id}`;
        return this.http.put<void>(url, dto);
    }

    addClinicalTrack(model: ClinicalTrackAddDto): Observable<void> {
        const url = `${this.coreApiPath}/clinical/clinical-track-configurations`;
        return this.http.post<void>(url, model);
    }

    uploadLicenseVerificationFile(submittalJobId: number, file: File): Observable<void> {
        const url = `${this.coreApiPath}/clinical/license-verifications/${submittalJobId}`;
        const data = new FormData();
        data.append('file', file);
        return this.http.post<void>(url, data);
    }

    getLicenseVerificationContents(submittalJobId: number): Observable<HttpResponse<ArrayBuffer>> {
        const url = `${this.coreApiPath}/clinical/license-verifications/${submittalJobId}`;
        return this.http.get(url, { observe: 'response', responseType: 'arraybuffer' });
    }

    getClinicalReviewLinkDetails(submittalJobId: number): Observable<ClinicalReviewLinkDetails> {
        const url = `${this.coreApiPath}/clinical/clinicalReviews/link-details/${submittalJobId}`;
        return this.http.get<ClinicalReviewLinkDetails>(url);
    }

    getClinicalReviewNoteDetails(submittalJobId: number): Observable<ClinicalReviewNoteDetails> {
        const url = `${this.coreApiPath}/clinical/clinicalReviews/note-details/${submittalJobId}`;
        return this.http.get<ClinicalReviewNoteDetails>(url);
    }

    getClinicalReviewHistory(userId: number): Observable<ClinicalReviewHistory[]> {
        const url = `${this.coreApiPath}/clinical/clinical-review-history/${userId}`;
        return this.http.get<ClinicalReviewHistory[]>(url);
    }

    getClinicalReviewHistoryByFacility(
        userId: number,
        facilityId: number,
        submittalJobId: number
    ): Observable<ClinicalReviewHistoryByFacility[]> {
        const url = `${this.coreApiPath}/clinical/clinical-review-history/${userId}/facility/${facilityId}?submittalJobId=${submittalJobId}`;
        return this.http.get<ClinicalReviewHistoryByFacility[]>(url);
    }

    getClinicalReviewMatchHistory(
        submittalId: number,
        candidateUserId: number,
        jobId: number
    ): Observable<SubmittalJobQualifications> {
        const url = `${this.coreApiPath}/clinical/clinical-review-history/match-history/${submittalId}/${candidateUserId}/${jobId}`;
        return this.http.get<SubmittalJobQualifications>(url);
    }

    saveClinicalReviewMatchHistory(data: ClinicalReviewMatchHistory): Observable<number> {
        const url = `${this.coreApiPath}/clinical/clinical-review-history/match-history`;
        return this.http.post<number>(url, data);
    }

    sendSentToClinicalQueueEmail(submittalId: number, jobId: number): Observable<boolean> {
        return this.http.post<boolean>(
            `${this.coreApiPath}/clinical-submittal-emails/sent-to-queue/${submittalId}/${jobId}`,
            null
        );
    }

    public searchReviews(args: FetchArgs): Observable<PagingToken<ClinicalReview[]>> {
        let url = `${this.coreApiPath}/clinical/clinicalreviews`;
        const { pageSize, skip } = args;
        if (args.filter) {
            for (const filterName in args.filter) {
                if (Object.prototype.hasOwnProperty.call(args.filter, filterName)) {
                    // for the eslint gaurd-for-in
                    const val =
                        filterName === 'lineOfBusinessIds'
                            ? args.filter[filterName]
                            : encodeURIComponent(args.filter[filterName]);

                    if (val === 'null' || (Array.isArray(val) && val?.length === 0)) {
                        continue;
                    }

                    if (Array.isArray(val)) {
                        url = appendQuery(url, arrayToQuery(`filter.${filterName}`, val as any[]));
                    } else if (val) {
                        url = appendQuery(url, `filter.${filterName}=${val}`);
                    }
                }
            }
        }

        if (args.sort && args.sort.field.length) {
            url = appendQuery(url, `sort.field=${args.sort.field}&sort.type=${args.sort.dir || 'asc'}`);
        }
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.http.get<PagingToken<ClinicalReview[]>>(path);
    }

    public assignClinicalReviews(assigns: AssignClinicalReviews[]) {
        const url = `${this.coreApiPath}/clinical/clinicalreviews`;
        return this.http.post(url, assigns);
    }

    public reassignClinicalReviews(assigns: ReassignClinicalReviews[]) {
        const url = `${this.coreApiPath}/clinical/clinicalreviews/reassign`;
        return this.http.post(url, assigns);
    }

    public unassignClinicalReviews(unassigns: number[]) {
        const url = `${this.coreApiPath}/clinical/clinicalreviews`;
        return this.http.delete(url, { body: unassigns });
    }

    public getClinicalReviewReassignReasonLookup(): Observable<ClinicalReviewReassignReasonLookup> {
        const url = `${this.coreApiPath}/clinical/clinicalreviews/clinical-review-reassign-reason-lookup`;
        return this.http.get<ClinicalReviewReassignReasonLookup>(url);
    }

    public getUdConfigurations(args: FetchArgs): Observable<PagingToken<UdConfiguration[]>> {
        let url = `${this.coreApiPath}/clinical/Unit-Description-Configuration`;
        const queryString = fetchArgsToQueryString(args);
        if (queryString.length) {
            url += queryString;
        }
        return this.http.get<PagingToken<UdConfiguration[]>>(url);
    }

    public getProfessionSpecialtyAssessments(): Observable<ProfessionSpecialtyAssessment[]> {
        const url = `${this.coreApiPath}/clinical/Unit-Description-Configuration/profession-specialty-assessments`;
        return this.http.get<ProfessionSpecialtyAssessment[]>(url);
    }

    public getCertsAndSkillsByAssessment(
        professionId: number,
        expertiseId: number,
        assessmentId: number
    ): Observable<UdAssessmentCertsAndSkills> {
        const url = `${this.coreApiPath}/clinical/Unit-Description-Configuration/assessment/${professionId}/${expertiseId}/${assessmentId}`;
        return this.http.get<UdAssessmentCertsAndSkills>(url);
    }

    public getRelatedSpecialtiesByAssessment(selectedSpecialtyProfessionAssessmentId: number, assessmentId: number) {
        const url = `${this.coreApiPath}/clinical/Unit-Description-Configuration/related-specialties/${selectedSpecialtyProfessionAssessmentId}/${assessmentId}`;
        return this.http.get<LookupDto[]>(url);
    }

    public getUnitDescriptionConfigurationById(id: number): Observable<UdConfigurationDetails> {
        const url = `${this.coreApiPath}/clinical/Unit-Description-Configuration/${id}`;
        return this.http.get<UdConfigurationDetails>(url);
    }

    public getCertifications(): Observable<LookupDto[]> {
        const url = `${this.coreApiPath}/clinical/Unit-Description-Configuration/certifications`;
        return this.http.get<LookupDto[]>(url);
    }

    public upsertUnitDescriptionConfiguration(dto: UpsertUnitDescriptionConfigurationDto): Observable<void> {
        const url = `${this.coreApiPath}/clinical/Unit-Description-Configuration`;
        return this.http.post<void>(url, dto);
    }

    protected getPaged<T>(pageSize: number, skip: number, url: string): Observable<PagingToken<T[]>> {
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.http.get<PagingToken<T[]>>(path, { observe: 'response' }).pipe(
            map((resp) => {
                return resp.body;
            })
        );
    }

    public getInProgressClinicalReview(args: FetchArgs): Observable<PagingToken<InProgressReviewQueueDto[]>> {
        const skipParam = 'skip';
        const pageSizeParam = 'pageSize';
        const sortfieldParam = 'sort.field';
        const sortdirParam = 'sort.dir';
        const filterCandidateUserId = 'filter.candidateUserId';
        const filterCandiadateName = 'filter.candidateName';
        const filterAssignedUserId = 'filter.assignedUserId';
        const filterAssignedUserName = 'filter.assignedUserName';
        const filterRecruiterName = 'filter.recruiterName';
        const filterPriority = 'filter.priority';
        const facilityName = 'filter.facilityName';
        const hospitalSystemName = 'filter.hospitalSystemName';
        const LineOfBusinessIds = 'filter.LineOfBusinessIds';
        let params = new HttpParams()
            .set(pageSizeParam, args.pageSize.toString())
            .set(skipParam, args.skip.toString())
            .set(sortfieldParam, args.sort?.field)
            .set(sortdirParam, args.sort?.dir)
            .set(filterCandidateUserId, args.filter.candidateUserId ?? 0)
            .set(filterCandiadateName, args.filter.candidateName ?? '')
            .set(filterAssignedUserId, args.filter.assignedUserId ?? 0)
            .set(filterAssignedUserName, args.filter.assignedUserName ?? '')
            .set(filterRecruiterName, args.filter.recruiterName ?? '')
            .set(filterPriority, args.filter.priority ?? '')
            .set(facilityName, args.filter.facilityName ?? '')
            .set(hospitalSystemName, args.filter.hospitalSystemName ?? '');
        if (args.filter.lineOfBusiness && args.filter.lineOfBusiness.length > 0) {
            args.filter.lineOfBusiness.forEach((id) => {
                params = params.append(LineOfBusinessIds, id.toString());
            });
        }

        const url = `${this.coreApiPath}/Clinical/ClinicalReviews/in-progresses`;
        return this.http.get<PagingToken<InProgressReviewQueueDto[]>>(url, { params });
    }

    public getClinicalReviewSubmittalStatuses(): Observable<ClinicalReviewSubmittalStatus[]> {
        const url = `${this.coreApiPath}/Clinical/ClinicalReviews/clinical-review-statuses`;
        return this.http.get<ClinicalReviewSubmittalStatus[]>(url);
    }

    public getLineOfBusinessLookup(): Observable<LineOfBusinessDto[]> {
        const url = `${this.coreApiPath}/Clinical/ClinicalReviews/line-of-businesses`;
        return this.http.get<LineOfBusinessDto[]>(url);
    }

    public getLineOfBusinessSearchAllReviewsLookup(): Observable<LineOfBusinessDto[]> {
        const url = `${this.coreApiPath}/Clinical/ClinicalReviews/line-of-businesses-all`;
        return this.http.get<LineOfBusinessDto[]>(url);
    }
}
